<main class="verification-case-page p-4 p-xxl-5">
  <template v-if="!is_detail_page">
    <section class="card border rounded-3 overflow-hidden">
      <div v-if="run_feedback" :class="['alert rounded-0 border-0 border-bottom mb-0', feedback_alert_class()]">
        {{ run_feedback }}
      </div>

      <div class="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
        <div class="d-inline-flex align-items-center flex-wrap border rounded-2 bg-light p-1" role="group" aria-label="Verification filters">
          <button
            v-for="filter in filters"
            :key="filter.label"
            type="button"
            @click="set_active_filter(filter.tone)"
            :class="[
              'btn btn-sm d-inline-flex align-items-center gap-2 px-3 py-1',
              active_filter === filter.tone ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
            ]"
          >
            <span>{{ filter.label }}</span>
            <span v-if="filter.count">{{ filter.count }}</span>
          </button>
        </div>

        <div class="d-flex align-items-center justify-content-end flex-wrap gap-2">
          <label class="onboarding-queue-search d-inline-flex align-items-center">
            <i class="bi bi-search" aria-hidden="true"></i>
            <input v-model="table_search" type="search" placeholder="Search table" @input="reset_table_page">
          </label>
          <button type="button" class="btn btn-sm bg-white border rounded-2 d-inline-flex align-items-center gap-2 fw-semibold px-3 py-1">
            <i class="bi bi-funnel"></i>
            <span>Filters</span>
          </button>
          <button type="button" class="btn btn-sm bg-white border rounded-2 d-inline-flex align-items-center gap-2 fw-semibold px-3 py-1">
            <i class="bi bi-download"></i>
            <span>Export</span>
          </button>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="text-uppercase text-muted small fw-semibold py-3 ps-4">Onboarded Entity</th>
              <th class="text-uppercase text-muted small fw-semibold py-3">Type</th>
              <th class="text-uppercase text-muted small fw-semibold py-3">Checks</th>
              <th class="text-uppercase text-muted small fw-semibold py-3">Status</th>
              <th class="text-uppercase text-muted small fw-semibold py-3">Owner</th>
              <th class="text-uppercase text-muted small fw-semibold py-3 pe-4">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="is_loading_checks">
              <td colspan="6" class="text-center text-muted py-5">Loading verification cases...</td>
            </tr>
            <tr v-else-if="checks_error">
              <td colspan="6" class="py-4">
                <div class="alert alert-warning mb-0">{{ checks_error }}</div>
              </td>
            </tr>
            <tr v-else-if="case_rows.length === 0">
              <td colspan="6" class="text-muted py-4 ps-4">No verification cases found.</td>
            </tr>
            <tr
              v-else
              v-for="row in paginated_case_rows"
              :key="row.id"
              class="cursor-pointer"
              tabindex="0"
              @click="open_case_summary(row)"
              @keyup.enter="open_case_summary(row)"
            >
              <td class="ps-4 py-3">
                <div class="fw-bold text-black">{{ row.case_name }}</div>
                <div class="text-muted small mt-1">{{ row.open_count }} open of {{ row.verification_count }} verification<span v-if="row.verification_count !== 1">s</span></div>
              </td>
              <td class="py-3">{{ row.type }}</td>
              <td class="py-3">{{ row.verification_count }}</td>
              <td class="py-3">
                <span :class="['badge rounded-pill fw-normal', status_badge_class(row.status_tone)]">
                  {{ row.status }}
                </span>
              </td>
              <td class="py-3">{{ row.reviewer }}</td>
              <td class="pe-4 py-3">
                <button
                  type="button"
                  class="onboarding-queue-view-button"
                  @click.stop="open_check_detail(row)"
                >
                  <span>View</span>
                  <i class="bi bi-arrow-right-short" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <TablePagination
        :page="table_page"
        :page-size="table_page_size"
        :page-size-options="table_page_size_options"
        :total-rows="table_total_rows"
        :page-start="table_page_start"
        :page-end="table_page_end"
        :total-pages="table_total_pages"
        @update:page="set_table_page"
        @update:page-size="set_table_page_size"
      />
    </section>

    <div
      v-if="selected_case_summary"
      class="onboarding-case-detail-backdrop"
      @click="close_case_summary"
    ></div>

    <aside
      v-if="selected_case_summary"
      :class="['onboarding-case-detail-drawer bg-white shadow-lg', { 'is-fullscreen': is_summary_detail_fullscreen }]"
    >
      <header class="border-bottom p-4">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="text-uppercase text-muted fw-semibold small mb-3">
              {{ selected_case_summary.journey || 'Onboarding' }} onboarding &middot; {{ is_manual_page ? 'Manual Verification gate' : is_field_verification_page ? 'Field Verification gate' : 'Verification gate' }}
            </div>
            <div class="d-flex align-items-center gap-2 flex-wrap mb-2">
              <h2 class="h5 fw-bold text-black mb-0">{{ selected_case_summary.name || 'Onboarding case' }}</h2>
              <EntityNameEditButton
                :entity-record="selected_case || selected_case_summary"
                :current-name="selected_case_summary.name || 'Onboarding case'"
                @updated="if (selected_case_summary) selected_case_summary.name = $event.name; if (selected_case) selected_case.name = $event.name"
              />
            </div>
            <p class="mb-0 text-muted">{{ selected_case_summary_subtitle() }}</p>
          </div>

          <div class="d-flex align-items-center gap-3 flex-shrink-0">
            <span :class="['badge rounded-pill fw-normal', status_badge_class(selected_case_summary.activation_tone || 'warning')]">
              <span class="me-1">&bull;</span>{{ selected_case_summary.activation || selected_case_summary.stage || 'Applicant' }}
            </span>
            <button
              type="button"
              class="btn btn-link text-muted p-0"
              :aria-label="is_summary_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
              :title="is_summary_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
              @click="toggle_summary_detail_fullscreen"
            >
              <i :class="['bi', is_summary_detail_fullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen', 'fs-5']"></i>
            </button>
            <button type="button" class="btn btn-link text-muted p-0" aria-label="Close entity detail" @click="close_case_summary">
              <i class="bi bi-x-lg fs-5"></i>
            </button>
          </div>
        </div>
      </header>

      <div class="onboarding-case-detail-body p-4">
        <nav class="bg-light rounded-3 p-1 d-flex align-items-center gap-2 mb-4" aria-label="Entity detail sections">
            <button
              v-for="tab in summary_detail_tabs"
              :key="tab.id"
              type="button"
              :class="[
                'btn btn-sm flex-fill d-inline-flex align-items-center justify-content-center gap-2',
                active_summary_detail_tab === tab.id ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
              ]"
              @click="set_summary_detail_tab(tab.id)"
            >
              <i :class="['bi', tab.icon]"></i>
              <span>{{ tab.label }}</span>
              <span v-if="tab.count" class="badge rounded-pill text-bg-danger">{{ tab.count }}</span>
            </button>
        </nav>

        <section v-if="active_summary_detail_tab === 'summary'">
          <div v-if="is_loading_summary_detail" class="text-muted mb-4">Loading entity detail...</div>

          <dl class="row g-0 mb-4">
            <template v-for="row in summary_rows" :key="row.label">
              <dt class="col-4 text-muted fw-normal mb-3">{{ row.label }}</dt>
              <dd class="col-8 fw-semibold mb-3">{{ row.value }}</dd>
            </template>
          </dl>

          <section class="mb-4">
            <div class="text-uppercase text-muted fw-semibold small mb-3">Overall readiness</div>
            <div class="d-flex align-items-center gap-3">
              <div class="progress flex-grow-1 onboarding-progress-track">
                <div
                  class="progress-bar onboarding-progress-bar"
                  :style="{ '--onboarding-progress-size': `${selected_case_summary.progress || 0}%` }"
                ></div>
              </div>
              <span class="small fw-bold">{{ selected_case_summary.progress || 0 }}%</span>
            </div>
          </section>

          <section>
            <div class="text-uppercase text-muted fw-semibold small mb-3">Stage timeline</div>
            <div class="onboarding-case-timeline">
              <article
                v-for="item in summary_stage_timeline"
                :key="item.title"
                :class="['onboarding-case-timeline-item', `is-${item.status}`]"
              >
                <span class="onboarding-case-timeline-icon">
                  <i :class="['bi', item.icon]"></i>
                </span>
                <div>
                  <div class="fw-semibold text-black">{{ item.title }}</div>
                  <div class="text-muted small mt-1">{{ item.detail }}</div>
                </div>
              </article>
            </div>
          </section>
        </section>

        <section v-else-if="active_summary_form_section">
          <div class="text-uppercase text-muted fw-semibold small mb-3">{{ active_summary_form_section.label }}</div>
          <section
            v-for="sub_section in active_summary_form_section.sub_sections"
            :key="sub_section.id"
            class="mb-4"
          >
            <div
              v-if="sub_section.label && sub_section.label !== active_summary_form_section.label"
              class="text-uppercase text-muted fw-semibold small mb-3"
            >
              {{ sub_section.label }}
            </div>

            <div class="onboarding-answer-grid">
              <article
                v-for="question in sub_section.questions"
                :key="question.id"
                class="onboarding-answer-card"
              >
                <p class="onboarding-answer-card__label">
                  {{ question.label }}
                  <span v-if="question.is_mandatory" class="text-danger">*</span>
                </p>
                <div class="onboarding-answer-card__value">{{ question.answer }}</div>
              </article>
            </div>
          </section>
        </section>
      </div>

      <footer
        v-if="!is_entity_verification_read_only"
        class="onboarding-case-detail-footer border-top bg-white p-3 d-flex align-items-center justify-content-between gap-3"
      >
        <button type="button" class="btn btn-link text-muted d-inline-flex align-items-center gap-2 text-decoration-none">
          <i class="bi bi-stars"></i>
          <span>Ask AI</span>
        </button>
        <div v-if="!is_entity_verification_read_only" class="d-flex align-items-center gap-2">
            <button
            type="button"
            class="btn btn-outline-danger d-inline-flex align-items-center gap-2"
            :disabled="workflow_action_in_progress === 'reject' || workflow_actions_loading"
            @click="run_selected_workflow_action('reject')"
          >
            <i class="bi bi-x-circle"></i>
            <span>{{ workflow_action_in_progress === 'reject' ? 'Rejecting...' : 'Reject' }}</span>
          </button>
          <button
            type="button"
            class="btn btn-outline-warning d-inline-flex align-items-center gap-2"
            :disabled="workflow_action_in_progress === 'request_info' || workflow_actions_loading"
            @click="run_selected_workflow_action('request_info')"
          >
            <i class="bi bi-send"></i>
            <span>{{ workflow_action_in_progress === 'request_info' ? 'Sending...' : 'Request info' }}</span>
          </button>
          <button
            v-if="can_advance_stage"
            type="button"
            class="btn btn-primary d-inline-flex align-items-center gap-2"
            :disabled="workflow_action_in_progress === 'advance' || workflow_actions_loading"
            @click="run_selected_workflow_action('advance')"
          >
            <i class="bi bi-arrow-right"></i>
            <span>{{ workflow_action_in_progress === 'advance' ? 'Advancing...' : 'Advance stage' }}</span>
          </button>
        </div>
      </footer>
      <div
        v-if="!is_entity_verification_read_only && (workflow_actions_loading || workflow_actions_error || workflow_action_feedback)"
        class="px-3 pb-3"
      >
        <div v-if="workflow_actions_loading" class="small text-muted">Loading workflow actions...</div>
        <div v-else-if="workflow_actions_error" class="alert alert-warning mb-0 py-2 px-3 small">{{ workflow_actions_error }}</div>
        <div
          v-else-if="workflow_action_feedback"
          :class="['alert mb-0 py-2 px-3 small', workflow_action_feedback_tone === 'success' ? 'alert-success' : workflow_action_feedback_tone === 'danger' ? 'alert-danger' : 'alert-warning']"
        >
          {{ workflow_action_feedback }}
        </div>
      </div>
    </aside>
  </template>

  <template v-else>
    <template v-if="uses_manual_style_workspace">
      <div v-if="run_feedback" :class="['alert border-0 mb-4', feedback_alert_class()]">
        {{ run_feedback }}
      </div>

      <section v-if="is_loading_checks || is_loading_detail_case" class="card border rounded-3 p-5 text-center text-muted">
        Loading {{ is_field_verification_page ? 'field verification' : 'manual verification' }} workspace...
      </section>

      <section v-else-if="checks_error" class="card border rounded-3 p-4">
        <div class="alert alert-warning mb-0">{{ checks_error }}</div>
      </section>

      <section v-else-if="!selected_case" class="card border rounded-3 p-5 text-center text-muted">
        No {{ is_field_verification_page ? 'field verification' : 'manual verification' }} case was found for this application.
      </section>

      <section v-else class="verification-manual-workspace">
        <div class="card border rounded-3 mb-4">
          <div class="card-body py-3">
            <div class="workflow-stepper">
              <div
                v-for="(step, index) in case_journey_steps"
                :key="`${step.gate_id}-${index}`"
                class="workflow-stepper__item"
              >
                <button
                  type="button"
                  class="btn border-0 bg-transparent p-0 text-center workflow-stepper__trigger"
                  :disabled="step.state !== 'completed' || !step.can_navigate"
                  @click="open_gate_journey_step(step)"
                >
                  <div class="workflow-stepper__content">
                    <span
                      :class="[
                        'rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0',
                        'workflow-step-circle',
                        step.state === 'completed' ? 'bg-success text-white' : step.state === 'current' ? 'border border-success text-success bg-white' : 'bg-light text-muted'
                      ]"
                    >
                      <i v-if="step.state === 'completed'" class="bi bi-check-lg" aria-hidden="true"></i>
                      <span v-else>{{ index + 1 }}</span>
                    </span>
                    <span :class="['workflow-stepper__label fw-semibold', step.state === 'upcoming' ? 'text-muted' : 'text-black']">{{ step.label }}</span>
                    <span class="workflow-stepper__meta small text-muted">{{ step.state === 'completed' ? 'Completed' : step.state === 'current' ? 'Current' : 'Upcoming' }}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="verification-case-page-header gate-overview-card mb-4">
          <div class="d-flex align-items-start justify-content-between flex-wrap p-3">
            <div class="d-flex align-items-center gap-3 min-w-0 p-3">
              <span class="gate-overview-card__avatar config-navbar-initials rounded-circle d-inline-flex align-items-center justify-content-center fw-bold">
                {{ selected_case?.name?.charAt(0) || 'V' }}
              </span>
              <div class="min-w-0">
                <div class="gate-overview-card__eyebrow">{{ is_field_verification_page ? 'Applicant field verification review' : 'Applicant verification review' }}</div>
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <h1 class="verification-manual-hero__name mb-0">{{ selected_case?.name || '-' }}</h1>
                  <span class="verification-manual-employee-badge text-xxs">{{ get_manual_workspace_summary_value([selected_case?.journey], 'Employee') }}</span>
                </div>
                <p class="gate-overview-card__meta mb-0 text-xs">
                  {{ get_manual_workspace_summary_value([selected_case?.journey], 'Journey') }} applicant
                  <span v-if="manual_workspace_item?.verification_name || manual_workspace_item?.check">
                    &middot; {{ manual_workspace_item?.verification_name || manual_workspace_item?.check }}
                  </span>
                </p>
              </div>
            </div>
            <span :class="['badge rounded-pill fw-normal px-3 py-1 mt-3', status_badge_class(manual_workspace_item?.status_tone || 'warning')]">
              {{ manual_workspace_badge_label }}
            </span>
          </div>
        </div>

        <div class="verification-manual-shell verification-case-history-shell">
          <div class="verification-case-history-layout">
            <aside class="verification-case-history-sidebar">
              <section class="verification-case-history-pinned" aria-label="Pinned action">
                <div class="verification-case-history-panel__eyebrow">Pinned</div>
                <button
                  v-if="manual_current_tab"
                  type="button"
                  :class="[
                    'verification-case-history-pinned-card',
                    { 'is-active': active_manual_section === manual_current_tab.id }
                  ]"
                  @click="set_active_manual_section(manual_current_tab.id)"
                >
                  <span class="verification-case-history-pinned-card__icon" aria-hidden="true"></span>
                  <span class="verification-case-history-pinned-card__body">
                    <span class="verification-case-history-pinned-card__title">Required Action</span>
                    <span class="verification-case-history-pinned-card__copy">{{ manual_required_action_sidebar_copy }}</span>
                  </span>
                  <span class="verification-case-history-pinned-card__count">1</span>
                </button>
              </section>

              <section class="verification-case-history-panel" aria-label="Case history">
                <div class="verification-case-history-panel__eyebrow">Onboarding History</div>

                <div class="verification-case-history-list">
                  <button
                    v-for="tab in manual_case_history_tabs"
                    :key="tab.id"
                    type="button"
                    :class="[
                      'verification-case-history-item',
                      {
                        'is-active': active_manual_section === tab.id,
                        'is-completed': tab.sidebar_state === 'completed',
                        'is-current': tab.sidebar_state === 'current'
                      }
                    ]"
                    @click="set_active_manual_section(tab.id)"
                  >
                    <span class="verification-case-history-item__marker" aria-hidden="true">
                      <i v-if="tab.sidebar_state === 'completed'" class="bi bi-check-lg"></i>
                      <span v-else>{{ tab.step_number || 1 }}</span>
                    </span>
                    <span class="verification-case-history-item__content">
                      <span class="verification-case-history-item__title">{{ tab.label }}</span>
                      <span :class="['verification-case-history-item__status', { 'is-current': tab.sidebar_state === 'current' }]">
                        {{ tab.sidebar_badge }}
                      </span>
                      <span class="verification-case-history-item__detail">
                        {{ tab.actor_label }} · {{ tab.timestamp_label }}
                      </span>
                    </span>
                  </button>
                </div>
              </section>

              <button
                v-if="manual_reference_tab"
                type="button"
                :class="[
                  'verification-case-history-reference',
                  { 'is-active': active_manual_section === manual_reference_tab.id }
                ]"
                @click="set_active_manual_section(manual_reference_tab.id)"
              >
                <span class="verification-case-history-reference__label">{{ manual_reference_tab.label }}</span>
                <span class="verification-case-history-reference__value">{{ manual_application_reference_value }}</span>
              </button>
            </aside>

            <div class="verification-case-history-content">
            <div :class="['verification-manual-tab-content', { 'verification-manual-tab-content--history': active_manual_shell_tab === 'history' }]">
              <nav class="verification-manual-tabs" aria-label="Manual verification sections">
                <button
                  v-for="tab in manual_shell_tabs"
                  :key="tab.id"
                  type="button"
                  :class="['verification-manual-tabs__tab', { 'is-active': active_manual_shell_tab === tab.id }]"
                  @click="set_active_manual_shell_tab(tab.id)"
                >
                  <span>{{ tab.label }}</span>
                  <span v-if="tab.badge" class="badge rounded-pill">{{ tab.badge }}</span>
                </button>
              </nav>
                <nav
                  v-if="active_manual_shell_tab === 'action' && manual_workspace_tabs.length && !active_manual_historical_gate_tab && active_manual_section !== 'manual-applicant-information'"
                  class="verification-manual-tabs verification-manual-subtabs"
                  aria-label="Manual verification workspace sections"
                >
                <button
                  v-for="tab in manual_workspace_tabs"
                  :key="tab.id"
                  type="button"
                  :class="['verification-manual-tabs__tab', { 'is-active': active_manual_section === tab.id }]"
                  @click="set_active_manual_section(tab.id)"
                >
                  <span>{{ tab.workspace_label || tab.label }}</span>
                  <span v-if="tab.badge" class="badge rounded-pill">{{ tab.badge }}</span>
                </button>
              </nav>

              <template v-if="active_manual_shell_tab === 'history'">
                <div class="verification-manual-info-card verification-history-rail-card">
                  <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                    <div>
                      <h2 class="verification-manual-section-title mb-1">Onboarding History</h2>
                      <p class="text-muted mb-0">Previous gates completed for this case.</p>
                    </div>
                  </div>

                  <div v-if="manual_history_only_tabs.length" class="verification-case-history-list">
                    <button
                      v-for="tab in manual_history_only_tabs"
                      :key="tab.id"
                      type="button"
                      :class="[
                        'verification-case-history-item',
                        {
                          'is-active': active_manual_section === tab.id,
                          'is-completed': tab.sidebar_state === 'completed',
                          'is-current': tab.sidebar_state === 'current'
                        }
                      ]"
                      @click="set_active_manual_section(tab.id)"
                    >
                      <span class="verification-case-history-item__marker" aria-hidden="true">
                        <i v-if="tab.sidebar_state === 'completed'" class="bi bi-check-lg"></i>
                        <span v-else>{{ tab.step_number || 1 }}</span>
                      </span>
                      <span class="verification-case-history-item__content">
                        <span class="verification-case-history-item__title">{{ tab.label }}</span>
                        <span :class="['verification-case-history-item__status', { 'is-current': tab.sidebar_state === 'current' }]">
                          {{ tab.sidebar_badge }}
                        </span>
                        <span class="verification-case-history-item__detail">
                          {{ tab.actor_label }} · {{ tab.timestamp_label }}
                        </span>
                      </span>
                    </button>
                  </div>
                  <div v-else class="text-muted">No previous gates have been completed for this case yet.</div>
                </div>

                <div v-if="active_manual_historical_gate_tab" class="verification-manual-info-card verification-history-detail-card">
                  <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                    <div>
                      <h2 class="verification-manual-section-title mb-1">{{ active_manual_historical_gate_tab.label }}</h2>
                      <p class="text-muted mb-0">Read-only context carried forward from this completed workflow step.</p>
                    </div>
                    <span :class="['badge rounded-pill fw-normal', status_badge_class(active_manual_historical_gate_status_tone)]">
                      {{ active_manual_historical_gate_status_label }}
                    </span>
                  </div>

                  <div class="row g-3 mb-3">
                    <div class="col-md-6 col-xl-3">
                      <div class="border rounded-3 px-3 py-2 bg-light h-100">
                        <div class="text-uppercase text-muted fw-semibold small mb-2">Action</div>
                        <div class="fw-semibold text-black">{{ active_manual_historical_gate_status_message }}</div>
                      </div>
                    </div>
                    <div class="col-md-6 col-xl-3">
                      <div class="border rounded-3 px-3 py-2 bg-light h-100">
                        <div class="text-uppercase text-muted fw-semibold small mb-2">Actioned by</div>
                        <div class="fw-semibold text-black">{{ active_manual_historical_gate_actor }}</div>
                      </div>
                    </div>
                    <div class="col-md-6 col-xl-3">
                      <div class="border rounded-3 px-3 py-2 bg-light h-100">
                        <div class="text-uppercase text-muted fw-semibold small mb-2">Completed</div>
                        <div class="fw-semibold text-black">{{ active_manual_historical_gate_completed_at }}</div>
                      </div>
                    </div>
                    <div class="col-md-6 col-xl-3">
                      <div class="border rounded-3 px-3 py-2 bg-light h-100">
                        <div class="text-uppercase text-muted fw-semibold small mb-2">Status</div>
                        <div class="fw-semibold text-black">{{ active_manual_historical_gate_status_label }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="border rounded-3 px-3 py-3 bg-light">
                    <div class="text-uppercase text-muted fw-semibold small mb-2">Reason</div>
                    <div class="text-black">{{ active_manual_historical_gate_reason }}</div>
                  </div>
                </div>
              </template>

              <template v-else-if="active_manual_section === 'manual-applicant-information'">
              <ApplicationDetailsReview :form_sections="get_manual_applicant_form_sections()" />
              </template>

            <div v-else-if="active_manual_section === 'manual-documents'" id="manual-documents">
              <h2 class="verification-manual-section-title">Submitted Documents</h2>
              <div v-if="!manual_workspace_documents.length" class="verification-manual-info-card text-muted">
                No applicant-submitted documents were returned by the backend.
              </div>
              <div class="verification-manual-document-grid">
                <div v-for="document in manual_workspace_documents" :key="document.id" class="verification-manual-document-card">
                  <div class="verification-manual-document-card__icon">
                    <i class="bi bi-file-earmark-pdf"></i>
                  </div>
                  <div class="min-w-0 flex-grow-1">
                    <div class="d-flex align-items-center justify-content-between gap-2">
                      <div class="verification-manual-document-card__title text-truncate">{{ document.name }}</div>
                      <span :class="['badge rounded-pill fw-normal', status_badge_class(document.status.tone)]">{{ document.status.label }}</span>
                    </div>
                    <div class="verification-manual-document-card__file text-truncate">{{ document.file_name || 'No file name' }}</div>
                    <div class="d-flex justify-content-end mt-2">
                      <button type="button" class="verification-manual-view-button" :disabled="!is_previewable_document(document)" @click="open_document_preview(document)">View <i class="bi bi-eye"></i></button>
                    </div>
                  </div>
                  <button type="button" class="verification-manual-document-menu" aria-label="Document options"><i class="bi bi-three-dots-vertical"></i></button>
                </div>
              </div>
            </div>

            <div v-else-if="active_manual_section === 'manual-verification-results'" id="manual-verification-results" class="verification-manual-info-card cards-border-color">
                <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                  <div class="verification-manual-card-heading mb-0">
                    <i class="bi bi-bank"></i>
                    <span>Bank Statement Analysis</span>
                  </div>
                </div>
              <div v-if="bank_statement_header_badges().length" class="d-flex align-items-center flex-wrap gap-2 text-muted small mb-3">
                <span
                  v-for="badge in bank_statement_header_badges()"
                  :key="badge.id"
                  class="d-inline-flex align-items-center gap-1"
                >
                  <span class="fw-semibold text-black">{{ badge.value }}</span>
                  <span>{{ badge.label }}</span>
                </span>
              </div>
              <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
                <div>
                  <div class="text-uppercase text-muted fw-semibold small mb-1">Analysis Status</div>
                  <div class="fw-semibold text-black">{{ bank_statement_status_label() }}</div>
                </div>
                <span :class="['badge rounded-pill fw-normal', status_badge_class(bank_statement_status_tone())]">
                  {{ bank_statement_status_label() }}
                </span>
              </div>
              <div v-if="bank_statement_is_running()" class="mb-4">
                <div class="d-flex align-items-center gap-3 mb-3">
                  <div class="spinner-border text-success" role="status" aria-hidden="true"></div>
                  <div>
                    <div class="fw-semibold text-black">{{ bank_statement_status_label() }}</div>
                    <div class="text-muted small">{{ bank_statement_status_message() }}</div>
                  </div>
                </div>
                <div class="progress mb-3" style="height: 6px;">
                  <div
                    class="progress-bar bg-success"
                    role="progressbar"
                    :style="{ width: `${bank_statement_progress_value()}%` }"
                    :aria-valuenow="bank_statement_progress_value()"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <div class="d-grid gap-2">
                  <div
                    v-for="step in bank_statement_progress_steps()"
                    :key="step.id"
                    class="d-flex align-items-center gap-2 small"
                  >
                    <i
                      :class="[
                        'bi',
                        step.state === 'completed'
                          ? 'bi-check-circle-fill text-success'
                          : step.state === 'current'
                            ? 'bi-record-circle text-success'
                            : step.state === 'failed'
                              ? 'bi-x-circle-fill text-danger'
                              : 'bi-circle text-muted'
                      ]"
                    ></i>
                    <span :class="step.state === 'pending' ? 'text-muted' : 'text-black'">{{ step.label }}</span>
                  </div>
                </div>
              </div>
              <div v-if="bank_statement_metric_cards().length" class="row g-3 mb-3">
                <div v-for="row in bank_statement_metric_cards()" :key="row.id" class="col-md-6">
                  <div class="border rounded-3 px-3 py-2 bg-white h-100">
                    <div class="text-uppercase text-muted fw-semibold small mb-1">{{ row.label }}</div>
                    <div :class="['fw-semibold', row.tone === 'success' ? 'text-success' : row.tone === 'danger' ? 'text-danger' : row.tone === 'warning' ? 'text-warning-emphasis' : 'text-black']">{{ row.value }}</div>
                  </div>
                </div>
              </div>
              <div v-if="bank_statement_warning_rows().length" class="mt-3">
                <div class="text-uppercase text-muted fw-semibold small mb-2">Analysis notes</div>
                <div class="d-grid gap-2">
                  <div
                    v-for="warning in bank_statement_warning_rows()"
                    :key="warning.id"
                    class="border rounded-3 px-3 py-2 bg-white small text-muted"
                  >
                    {{ warning.value }}
                  </div>
                </div>
              </div>
              <div v-else-if="!bank_statement_metric_cards().length" class="text-muted small">
                {{ bank_statement_status_message() }}
              </div>
            </div>

            <div v-else-if="active_manual_section === 'manual-review-checklist'" id="manual-review-checklist" class="verification-manual-info-card">
              <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                <div>
                  <h2 class="verification-manual-section-title mb-1">Assigned Manual Verification Questions</h2>
                  <p class="text-muted small mb-0">Only questions mapped to the configured manual verification form are shown here.</p>
                </div>
                <button
                  v-if="!is_entity_verification_read_only && !is_bank_statement_manual_workspace()"
                  type="button"
                  class="onboarding-queue-view-button"
                  :disabled="!manual_workspace_item || is_manual_check_running(manual_workspace_item)"
                  @click="open_manual_workspace_review(manual_workspace_item, $event)"
                >
                  <span>{{ manual_workspace_is_terminal() ? 'Review again' : 'Open review' }}</span>
                  <i class="bi bi-arrow-right-short" aria-hidden="true"></i>
                </button>
              </div>
              <div v-if="!manual_workspace_review_sections().length" class="text-muted">
                No manual verification questions were assigned to this manual verification form.
              </div>
              <div class="verification-manual-decision-panel verification-manual-decision-panel--compact mb-3">
                <div class="verification-manual-decision-header">
                  <h2 class="verification-manual-section-title mb-0">Reviewer Decision</h2>
                  <div
                    v-if="manual_decision_label()"
                    :class="['verification-manual-decision-summary', `is-${manual_decision_tone()}`]"
                  >
                    <i class="bi bi-check2-circle" aria-hidden="true"></i>
                    <span>Decision taken: {{ manual_decision_label() }}</span>
                  </div>
                </div>
                <div class="verification-manual-decision-grid">
                  <div>
                    <label class="verification-manual-decision-label">Comments (Required)</label>
                    <textarea
                      v-model="manual_workspace.reviewer_comments"
                      class="verification-manual-decision-textarea"
                      rows="2"
                      placeholder="Summarize what you reviewed, what matched, and any issue that affected the decision."
                      :readonly="is_entity_verification_read_only"
                    ></textarea>
                  </div>
                  <div v-if="!is_entity_verification_read_only">
                    <label class="verification-manual-decision-label">Decision (Required)</label>
                    <div class="verification-manual-decision-actions">
                      <button
                        type="button"
                        :class="['verification-manual-decision-button is-approve', { 'is-selected': current_manual_decision_action() === 'approve' }]"
                        :disabled="manual_workspace_is_terminal()"
                        :title="manual_field_choice_disabled_reason()"
                        @click="manual_workspace.decision_status = 'approve'; submit_manual_workspace_decision('approve')"
                      >
                        <i class="bi bi-hand-thumbs-up"></i>
                        <strong>Approve</strong>
                        <span>Move to Approved</span>
                      </button>
                      <button
                        type="button"
                        :class="['verification-manual-decision-button is-changes', { 'is-selected': current_manual_decision_action() === 'request_changes' }]"
                        :disabled="manual_workspace_is_terminal()"
                        :title="manual_field_choice_disabled_reason()"
                        @click="manual_workspace.decision_status = 'request_changes'; submit_manual_workspace_decision('request_changes')"
                      >
                        <i class="bi bi-shield-exclamation"></i>
                        <strong>Request Changes</strong>
                        <span>Return to Applicant</span>
                      </button>
                      <button
                        type="button"
                        :class="['verification-manual-decision-button is-reject', { 'is-selected': current_manual_decision_action() === 'reject' }]"
                        :disabled="manual_workspace_is_terminal()"
                        :title="manual_field_choice_disabled_reason()"
                        @click="manual_workspace.decision_status = 'reject'; submit_manual_workspace_decision('reject')"
                      >
                        <i class="bi bi-diamond"></i>
                        <strong>Reject</strong>
                        <span>Move to Rejected</span>
                      </button>
                    </div>
                  </div>
                  <div v-else class="verification-manual-readonly-note">
                    <i class="bi bi-lock" aria-hidden="true"></i>
                    <span>Manual verification decisions are view-only for your login.</span>
                  </div>
                </div>
              </div>
              <div v-if="manual_workspace_is_terminal()" class="verification-manual-readonly-note">
                <i class="bi bi-lock" aria-hidden="true"></i>
                <span>{{ manual_field_choice_disabled_reason() }}</span>
              </div>
              <div class="verification-manual-review-forms">
                <div
                  v-for="form_section in manual_workspace_review_sections()"
                  :key="form_section.id"
                  class="verification-manual-review-form"
                >
                  <div class="verification-manual-review-rule-header">
                    <div class="verification-manual-review-rule-header__icon">
                      <i class="bi bi-ui-checks"></i>
                    </div>
                    <div class="min-w-0">
                      <div class="verification-manual-review-rule-header__eyebrow">Manual verification rule</div>
                      <h3 class="verification-manual-review-rule-header__title">{{ form_section.form_name }}</h3>
                    </div>
                    <span class="verification-manual-review-rule-header__badge">Mapped review</span>
                  </div>
                  <template v-for="section in form_section.sections" :key="section.id">
                    <div class="verification-manual-review-section-title">{{ section.label }}</div>
                    <div
                      v-for="sub_section in section.sub_sections"
                      :key="sub_section.id"
                      class="verification-manual-review-subsection"
                    >
                      <div
                        v-if="sub_section.label && sub_section.label !== section.label"
                        class="verification-manual-review-subsection__title"
                      >
                        {{ sub_section.label }}
                      </div>
                      <div class="verification-manual-checklist">
                        <article v-for="item in sub_section.questions" :key="item.id" class="verification-manual-checklist-row">
                          <div class="verification-manual-checklist-row__main">
                            <div class="verification-manual-checklist-row__label">{{ item.label }}</div>
                            <div class="verification-manual-checklist-row__detail">{{ item.detail }}</div>
                          </div>
                          <div class="verification-manual-checklist-row__answer-panel">
                            <span>Submitted value</span>
                            <div class="verification-manual-checklist-row__answer-line">
                              <strong class="verification-manual-checklist-row__answer">{{ format_detail_value(item.answer) }}</strong>
                              <button
                                v-if="is_previewable_manual_question(item)"
                                type="button"
                                class="verification-manual-preview-button"
                                @click="open_manual_question_preview(item)"
                              >
                                Preview
                              </button>
                            </div>
                          </div>
                          <div v-if="!is_entity_verification_read_only" class="verification-manual-checklist-row__options">
                            <button
                              type="button"
                              :class="['verification-manual-review-choice', 'is-success', { 'is-selected': item.status === 'pass' }]"
                              :disabled="manual_field_choice_disabled(item)"
                              :title="manual_field_choice_disabled_reason()"
                              @click="record_manual_checklist_choice(item, 'match', $event)"
                            >
                              <i class="bi bi-check-circle"></i>
                              <span>Pass</span>
                            </button>
                            <button
                              type="button"
                              :class="['verification-manual-review-choice', 'is-danger', { 'is-selected': item.status === 'fail' }]"
                              :disabled="manual_field_choice_disabled(item)"
                              :title="manual_field_choice_disabled_reason()"
                              @click="record_manual_checklist_choice(item, 'mismatch', $event)"
                            >
                              <i class="bi bi-x-circle"></i>
                              <span>Fail</span>
                            </button>
                            <button
                              type="button"
                              :class="['verification-manual-review-choice', 'is-muted', { 'is-selected': item.status === 'na' }]"
                              :disabled="manual_field_choice_disabled(item)"
                              :title="manual_field_choice_disabled_reason()"
                              @click="record_manual_checklist_choice(item, 'unable_to_verify', $event)"
                            >
                              <i class="bi bi-dash-circle"></i>
                              <span>N/A</span>
                            </button>
                          </div>
                          <div v-if="item.comment" class="verification-manual-checklist-row__comment">{{ item.comment }}</div>
                        </article>
                      </div>
                    </div>
                  </template>
              </div>
            </div>
            </div>
          </div>
        </div>
          </div>
        </div>
      </section>
    </template>

    <template v-else>
      <header class="mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <button
          type="button"
          class="btn btn-link text-decoration-none px-0 d-inline-flex align-items-center gap-2 onboarding-text-navy verification-case-back-link"
          @click="go_back_to_verification_list"
          title="Click to go back, hold to see history"
        >
          <i class="bi bi-arrow-left"></i>
          <span>Back to {{ is_field_verification_page ? 'Field Verification' : 'Verification' }}</span>
        </button>
      </div>

        <div v-if="run_feedback" :class="['alert border-0 mb-4', feedback_alert_class()]">
          {{ run_feedback }}
        </div>

        <div v-if="case_journey_steps.length" class="card border rounded-3 mb-4">
          <div class="card-body py-3">
            <div class="workflow-stepper">
              <div
                v-for="(step, index) in case_journey_steps"
                :key="`${step.gate_id}-${index}`"
                class="workflow-stepper__item"
              >
                <button
                  type="button"
                  class="btn border-0 bg-transparent p-0 text-center workflow-stepper__trigger"
                  :disabled="step.state !== 'completed' || !step.can_navigate"
                  @click="open_gate_journey_step(step)"
                >
                  <div class="workflow-stepper__content">
                    <span
                      :class="[
                        'rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0',
                        'workflow-step-circle',
                        step.state === 'completed' ? 'bg-success text-white' : step.state === 'current' ? 'border border-success text-success bg-white' : 'bg-light text-muted'
                      ]"
                    >
                      <i v-if="step.state === 'completed'" class="bi bi-check-lg" aria-hidden="true"></i>
                      <span v-else>{{ index + 1 }}</span>
                    </span>
                    <span :class="['workflow-stepper__label fw-semibold', step.state === 'upcoming' ? 'text-muted' : 'text-black']">{{ step.label }}</span>
                    <span class="workflow-stepper__meta small text-muted">{{ step.state === 'completed' ? 'Completed' : step.state === 'current' ? 'Current' : 'Upcoming' }}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="verification-case-page-header gate-overview-card mb-4">
          <div class="d-flex align-items-start justify-content-between flex-wrap p-3">
            <div class="d-flex align-items-center gap-3 min-w-0">
              <span class="gate-overview-card__avatar config-navbar-initials rounded-circle d-inline-flex align-items-center justify-content-center fw-bold">
                {{ selected_case?.name?.charAt(0) || 'A' }}
              </span>
              <div class="min-w-0">
                <div class="gate-overview-card__eyebrow text-xxs">Automatic verification review</div>
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <h6 class="mb-0 fw-bold">{{ selected_case?.name || $route.query.name || 'Verification case' }}</h6>
                  <span class="verification-manual-employee-badge text-xs">{{ selected_case?.journey || 'Onboarding' }}</span>
                </div>
                <p class="gate-overview-card__meta mb-0">
                  {{ selected_case?.journey || 'Onboarding' }} applicant
                  <span v-if="selected_case?.workflow_display_stage || selected_case?.current_workflow_status || selected_case?.stage">
                    &middot; {{ selected_case?.workflow_display_stage || selected_case?.current_workflow_status || selected_case?.stage }}
                  </span>
                </p>
              </div>
            </div>
            <span :class="['badge rounded-pill fw-normal px-3 py-1', status_badge_class(selected_case?.activation_tone || 'warning')]">
              {{ selected_case?.workflow_display_stage || selected_case?.current_workflow_status || selected_case?.stage || 'Verification' }}
            </span>
          </div>
        </div>
    </header>

    <section v-if="is_loading_checks || is_loading_detail_case" class="card border rounded-3 p-5 text-center text-muted">
      Loading verification case...
    </section>

    <section v-else-if="checks_error" class="card border rounded-3 p-4">
      <div class="alert alert-warning mb-0">{{ checks_error }}</div>
    </section>

    <section v-else-if="!selected_case" class="card border rounded-3 p-5 text-center text-muted">
      No verification case was found for this application.
    </section>

      <section v-else class="verification-manual-workspace">
        <div class="verification-manual-shell verification-case-history-shell">
          <div class="verification-case-history-layout">
            <aside class="verification-case-history-sidebar">
              <section class="verification-case-history-pinned" aria-label="Pinned action">
                <div class="verification-case-history-panel__eyebrow">Pinned</div>

                <button
                  type="button"
                  :class="[
                    'verification-case-history-pinned-card',
                    { 'is-active': active_auto_section === 'auto-verification-results' }
                  ]"
                  @click="set_active_auto_section('auto-verification-results')"
                >
                  <span class="verification-case-history-pinned-card__icon" aria-hidden="true"></span>
                  <span class="verification-case-history-pinned-card__body">
                    <span class="verification-case-history-pinned-card__title">Required Action</span>
                    <span class="verification-case-history-pinned-card__copy">{{ auto_required_action_sidebar_copy }}</span>
                  </span>
                  <span class="verification-case-history-pinned-card__count">1</span>
                </button>
              </section>

              <section class="verification-case-history-panel" aria-label="Case history">
                <div class="verification-case-history-panel__eyebrow">Onboarding History</div>

                <div class="verification-case-history-list">
                  <button
                    v-for="tab in auto_case_history_tabs"
                    :key="tab.id"
                    type="button"
                    :class="[
                      'verification-case-history-item',
                      `is-${tab.sidebar_state}`,
                      { 'is-active': active_auto_section === tab.id }
                    ]"
                    @click="set_active_auto_section(tab.id)"
                  >
                    <span class="verification-case-history-item__marker" aria-hidden="true">
                      <i v-if="tab.sidebar_state === 'completed'" class="bi bi-check-lg"></i>
                      <span v-else>{{ tab.step_number || '•' }}</span>
                    </span>
                    <span class="verification-case-history-item__content">
                      <span class="verification-case-history-item__title">{{ tab.label }}</span>
                      <span :class="['verification-case-history-item__status', { 'is-current': tab.sidebar_state === 'current' }]">
                        {{ tab.sidebar_badge }}
                      </span>
                      <span class="verification-case-history-item__detail">
                        {{ tab.actor_label }}<span v-if="tab.timestamp_label && tab.timestamp_label !== '-'"> · {{ tab.timestamp_label }}</span>
                      </span>
                    </span>
                  </button>
                </div>
              </section>

              <button
                v-if="auto_reference_tab"
                type="button"
                :class="[
                  'verification-case-history-reference',
                  { 'is-active': active_auto_section === auto_reference_tab.id }
                ]"
                @click="set_active_auto_section(auto_reference_tab.id)"
              >
                <span class="verification-case-history-reference__label">{{ auto_reference_tab.label }}</span>
                <span class="verification-case-history-reference__value">{{ auto_application_reference_value }}</span>
              </button>
            </aside>

            <div class="verification-case-history-content">
              <div :class="['verification-manual-tab-content', { 'verification-manual-tab-content--history': active_auto_shell_tab === 'history' }]">
        <nav class="verification-manual-tabs" aria-label="Automatic verification sections">
          <button
            v-for="tab in auto_shell_tabs"
            :key="tab.id"
            type="button"
            :class="['verification-manual-tabs__tab', { 'is-active': active_auto_shell_tab === tab.id }]"
            @click="set_active_auto_shell_tab(tab.id)"
          >
            <span>{{ tab.label }}</span>
            <span v-if="tab.badge" class="badge rounded-pill">{{ tab.badge }}</span>
          </button>
        </nav>

        <div v-if="active_auto_shell_tab === 'history'" class="verification-manual-info-card verification-history-rail-card">
          <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
            <div>
              <h2 class="verification-manual-section-title mb-1">Onboarding History</h2>
              <p class="text-muted mb-0">Previous gates completed for this case.</p>
            </div>
          </div>

          <div v-if="auto_history_only_tabs.length" class="verification-case-history-list">
            <button
              v-for="tab in auto_history_only_tabs"
              :key="tab.id"
              type="button"
              :class="[
                'verification-case-history-item',
                `is-${tab.sidebar_state}`,
                { 'is-active': active_auto_section === tab.id }
              ]"
              @click="set_active_auto_section(tab.id)"
            >
              <span class="verification-case-history-item__marker" aria-hidden="true">
                <i v-if="tab.sidebar_state === 'completed'" class="bi bi-check-lg"></i>
                <span v-else>{{ tab.step_number || '•' }}</span>
              </span>
              <span class="verification-case-history-item__content">
                <span class="verification-case-history-item__title">{{ tab.label }}</span>
                <span :class="['verification-case-history-item__status', { 'is-current': tab.sidebar_state === 'current' }]">
                  {{ tab.sidebar_badge }}
                </span>
                <span class="verification-case-history-item__detail">
                  {{ tab.actor_label }}<span v-if="tab.timestamp_label && tab.timestamp_label !== '-'"> · {{ tab.timestamp_label }}</span>
                </span>
              </span>
            </button>
          </div>
          <div v-else class="text-muted">No previous gates have been completed for this case yet.</div>
        </div>

        <div v-if="active_auto_shell_tab === 'history' && !active_historical_gate_tab" class="verification-manual-info-card verification-history-detail-card text-muted">
          Select a completed gate to view its details.
        </div>

        <template v-if="active_historical_gate_tab?.gate_id === 'manual-verification'">
          <ManualVerificationHistoryView
            :decision-action="previous_manual_decision_action()"
            :decision-actor="previous_manual_history_reviewer()"
            :decision-comment="previous_manual_history_comment()"
            :decision-label="previous_manual_decision_label()"
            :decision-tone="previous_manual_decision_tone()"
            :decision-timestamp="previous_manual_history_timestamp()"
            :status-label="previous_manual_history_status()"
            :review-sections="previous_manual_review_sections()"
            :field-runs="previous_manual_history_field_runs()"
            :show-history-tabs="false"
            :applicant-form-sections="get_manual_applicant_form_sections()"
            :documents="manual_workspace_documents"
            @preview-document="open_document_preview"
            @preview-question="open_manual_question_preview"
          />
        </template>

        <template v-else-if="active_historical_gate_tab && active_historical_gate_tab.gate_id !== 'verification'">
          <div class="card border rounded-3">
            <div class="card-body">
              <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                <div>
                  <h2 class="h5 mb-1">{{ active_historical_gate_tab.label }}</h2>
                  <p class="text-muted mb-0">Read-only context carried forward from this completed workflow step.</p>
                </div>
                <span :class="['badge rounded-pill fw-normal', status_badge_class(active_historical_gate_status_tone)]">
                  {{ active_historical_gate_status_label }}
                </span>
              </div>
              <div class="row g-3 mb-3">
                <div class="col-md-6 col-xl-3">
                  <div class="border rounded-3 px-3 py-2 bg-light h-100">
                    <div class="text-uppercase text-muted fw-semibold small mb-2">Action</div>
                    <div class="fw-semibold text-black">{{ active_historical_gate_status_message }}</div>
                  </div>
                </div>
                <div class="col-md-6 col-xl-3">
                  <div class="border rounded-3 px-3 py-2 bg-light h-100">
                    <div class="text-uppercase text-muted fw-semibold small mb-2">Actioned by</div>
                    <div class="fw-semibold text-black">{{ active_historical_gate_actor }}</div>
                  </div>
                </div>
                <div class="col-md-6 col-xl-3">
                  <div class="border rounded-3 px-3 py-2 bg-light h-100">
                    <div class="text-uppercase text-muted fw-semibold small mb-2">Completed</div>
                    <div class="fw-semibold text-black">{{ active_historical_gate_completed_at }}</div>
                  </div>
                </div>
                <div class="col-md-6 col-xl-3">
                  <div class="border rounded-3 px-3 py-2 bg-light h-100">
                    <div class="text-uppercase text-muted fw-semibold small mb-2">Status</div>
                    <div class="fw-semibold text-black">{{ active_historical_gate_status_label }}</div>
                  </div>
                </div>
              </div>
              <div class="border rounded-3 px-3 py-3 bg-light">
                <div class="text-uppercase text-muted fw-semibold small mb-2">Reason</div>
                <div class="text-black">{{ active_historical_gate_reason }}</div>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="active_historical_gate_tab && active_historical_gate_tab.gate_id === 'verification'">
          <div class="verification-manual-info-card verification-history-detail-card">
            <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-4">
              <div>
                <h2 class="h5 mb-1">{{ active_historical_gate_tab.label }}</h2>
                <p class="text-muted mb-0">Read-only verification results recorded for this completed workflow step.</p>
              </div>
              <span :class="['badge rounded-pill fw-normal', status_badge_class(active_historical_gate_status_tone)]">
                {{ active_historical_gate_status_label }}
              </span>
            </div>

            <HistoricalVerificationChecksView
              :checks="selected_case_checks"
              empty-message="No verification results were returned for this completed gate."
              :is-expanded="is_check_expanded"
              :get-check-passed-chips="get_check_passed_chips"
              :get-check-result-detail-rows="get_check_result_detail_rows"
              :get-check-history="get_check_history"
              :status-badge-class="status_badge_class"
              :evidence-icon-class="evidence_icon_class"
              :field-result-row-class="field_result_row_class"
              @toggle-check="toggle_check_expanded"
            />
          </div>
        </template>

        <template v-else-if="active_auto_section === 'auto-case-history'">
          <div class="verification-manual-info-card">
            <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
              <div>
                <h2 class="verification-manual-section-title mb-1">Onboarding History</h2>
                <p class="text-muted mb-0">Previous gates completed for this case.</p>
              </div>
            </div>

            <div v-if="auto_history_only_tabs.length" class="verification-case-history-list">
              <button
                v-for="tab in auto_history_only_tabs"
                :key="tab.id"
                type="button"
                :class="[
                  'verification-case-history-item',
                  `is-${tab.sidebar_state}`,
                  { 'is-active': active_auto_section === tab.id }
                ]"
                @click="set_active_auto_section(tab.id)"
              >
                <span class="verification-case-history-item__marker" aria-hidden="true">
                  <i v-if="tab.sidebar_state === 'completed'" class="bi bi-check-lg"></i>
                  <span v-else>{{ tab.step_number || '•' }}</span>
                </span>
                <span class="verification-case-history-item__content">
                  <span class="verification-case-history-item__title">{{ tab.label }}</span>
                  <span :class="['verification-case-history-item__status', { 'is-current': tab.sidebar_state === 'current' }]">
                    {{ tab.sidebar_badge }}
                  </span>
                  <span class="verification-case-history-item__detail">
                    {{ tab.actor_label }}<span v-if="tab.timestamp_label && tab.timestamp_label !== '-'"> · {{ tab.timestamp_label }}</span>
                  </span>
                </span>
              </button>
            </div>
            <div v-else class="text-muted">No previous gates have been completed for this case yet.</div>
          </div>
        </template>

          <template v-else-if="active_auto_section === 'manual-documents'">
            <div id="manual-documents">
              <h2 class="verification-manual-section-title">Submitted Documents</h2>
              <div v-if="!manual_workspace_documents.length" class="verification-manual-info-card text-muted">
                No applicant-submitted documents were returned by the backend.
              </div>
            <div class="verification-manual-document-grid">
              <div v-for="document in manual_workspace_documents" :key="document.id" class="verification-manual-document-card">
                <div class="verification-manual-document-card__icon">
                  <i class="bi bi-file-earmark-pdf"></i>
                </div>
                <div class="min-w-0 flex-grow-1">
                  <div class="d-flex align-items-center justify-content-between gap-2">
                    <div class="verification-manual-document-card__title text-truncate">{{ document.name }}</div>
                    <span :class="['badge rounded-pill fw-normal', status_badge_class(document.status.tone)]">{{ document.status.label }}</span>
                  </div>
                  <div class="verification-manual-document-card__file text-truncate">{{ document.file_name || 'No file name' }}</div>
                  <div class="d-flex justify-content-end mt-2">
                    <button type="button" class="verification-manual-view-button" :disabled="!is_previewable_document(document)" @click="open_document_preview(document)">View <i class="bi bi-eye"></i></button>
                  </div>
                </div>
                <button type="button" class="verification-manual-document-menu" aria-label="Document options"><i class="bi bi-three-dots-vertical"></i></button>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="active_auto_section === 'auto-applicant-details'">
        <ApplicationDetailsReview :form_sections="get_manual_applicant_form_sections()" />
        </template>

        <template v-else>
        <div class="verification-case-history-current-pill-row d-none">
          <button
            type="button"
            class="verification-case-history-current-pill"
            @click="set_active_auto_section('auto-verification-results')"
          >
            <span class="verification-case-history-current-pill__label">{{ auto_required_action_label }}</span>
            <span class="verification-case-history-current-pill__badge">Current</span>
          </button>
        </div>

        <p class="text-muted mb-2 px-2 mt-2">
          Run each verification type for this case and review the returned result before moving forward.
        </p>

        <div class="d-flex align-items-center justify-content-between mb-2 verification-case-shell__header p-2">
          <div class="text-uppercase fw-semibold small verification-case-shell__eyebrow">Verification checks ({{ selected_case_checks.length }})</div>
        </div>

        <div class="d-grid gap-3">
          <p v-if="selected_case_checks.length === 0" class="text-muted mb-0">
            No verification checks were returned for this case.
          </p>

          <article
            v-for="item in selected_case_checks"
            :key="item.id"
            :class="[
              'border-0 rounded-4 verification-check-card',
              `verification-check-card--${item.status_tone || 'secondary'}`
            ]"
          >
            <div class="verification-check-card__header">
              <button
                type="button"
                class="btn btn-link text-decoration-none text-start p-0 d-flex align-items-start gap-3 flex-grow-1 verification-check-card__toggle"
                @click="toggle_check_expanded(item)"
              >
                <span class="rounded-4 d-inline-flex align-items-center justify-content-center flex-shrink-0 verification-check-card__icon-shell">
                  <span
                    :class="[
                      'rounded-circle d-inline-flex align-items-center justify-content-center onboarding-icon-square-175 verification-check-card__icon',
                      evidence_icon_class(item.status_tone)
                    ]"
                  >
                    <i :class="['bi', verification_service_icon(item)]"></i>
                  </span>
                </span>
                <span class="min-w-0 flex-grow-1 verification-check-card__body">
                  <span class="d-block fw-bold text-black mb-1 verification-check-card__title">{{ item.check }}</span>
                  <span class="d-block small verification-check-card__meta">{{ item.source || 'Verification provider' }}</span>
                  <span v-if="get_check_passed_chips(item).length" class="verification-check-card__passed-chips">
                    <span
                      v-for="chip in get_check_passed_chips(item)"
                      :key="chip.key"
                      :class="['verification-check-card__result-chip', `is-${chip.state || 'pending'}`]"
                    >
                      {{ chip.label }}
                    </span>
                  </span>
                </span>
              </button>
              <div class="d-flex flex-column align-items-end gap-2 flex-shrink-0 verification-check-card__actions">
                <span :class="['badge rounded-pill fw-normal verification-check-card__status', status_badge_class(item.status_tone)]">
                  {{ item.status }}
                </span>
                <span class="verification-check-card__last-run">Last run: {{ item.last_run }}</span>
              </div>
            </div>

            <div class="verification-check-card__action-row">
              <button
                type="button"
                class="verification-check-card__action verification-check-card__action--secondary"
                @click="toggle_check_expanded(item)"
              >
                {{ is_check_expanded(item) ? 'Close' : 'Open' }}
              </button>
              <button
                v-if="can_run_auto_check(item)"
                type="button"
                class="verification-check-card__action verification-check-card__action--primary"
                :disabled="is_auto_check_running(item)"
                @click="run_auto_check(item, $event)"
              >
                {{ is_auto_check_running(item) ? 'Running...' : auto_check_action_label(item) }}
              </button>
              <button
                v-else-if="can_start_manual_review(item)"
                type="button"
                class="verification-check-card__action verification-check-card__action--primary"
                :disabled="is_manual_check_running(item)"
                @click="start_manual_review(item, $event)"
              >
                <i class="bi bi-play-circle" aria-hidden="true"></i>
                {{ is_manual_check_running(item) ? 'Opening...' : 'Start review' }}
              </button>
              <button
                v-else-if="can_complete_manual_review(item)"
                type="button"
                class="verification-check-card__action verification-check-card__action--primary"
                :disabled="is_manual_check_running(item)"
                @click="open_manual_review_modal(item, $event)"
              >
                <i class="bi bi-pencil-square" aria-hidden="true"></i>
                {{ is_manual_check_running(item) ? 'Loading...' : 'Complete review' }}
              </button>
            </div>

            <div v-if="is_check_expanded(item)" class="verification-check-card__expanded">
              <div v-if="get_check_result_detail_rows(item).length" class="verification-check-card__result-details">
                <div class="text-uppercase fw-semibold small mb-2 verification-check-card__section-label">Result details</div>
              <div class="table-responsive verification-result-detail-table-wrap">
                <table class="table align-middle mb-0 verification-result-detail-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Submitted value</th>
                      <th>Provider value</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="field in get_check_result_detail_rows(item)"
                      :key="field.key"
                      :class="field_result_row_class(field.match_state)"
                    >
                      <td class="fw-semibold text-black">{{ field.label }}</td>
                      <td>{{ field.user_answer }}</td>
                      <td>{{ field.api_value }}</td>
                      <td>
                        <span :class="['badge rounded-pill text-xs verification-result-detail-table__badge', status_badge_class(field.status_tone)]">
                          {{ field.status_label }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="get_manual_field_runs(item).length" class="mt-3">
              <div class="text-uppercase fw-semibold small mb-2 verification-check-card__section-label">Manual review details</div>
              <div class="d-grid gap-2">
                <div
                  v-for="field_run in get_manual_field_runs(item)"
                  :key="field_run.id"
                  class="border rounded-3 px-3 py-2 bg-light-subtle verification-check-card__detail-card"
                >
                  <div class="d-flex align-items-center justify-content-between gap-3">
                    <div class="fw-semibold text-black">{{ field_run.field_label_snapshot || format_field_label(field_run.section_question) }}</div>
                    <span :class="['badge rounded-pill fw-normal', status_badge_class(field_run.match_status === 'yes' ? 'success' : field_run.match_status === 'no' ? 'danger' : 'warning')]">
                      {{ format_match_status(field_run.match_status) }}
                    </span>
                  </div>
                  <div class="small text-muted mt-2">Submitted: {{ format_detail_value(field_run.user_answer) }}</div>
                  <div class="small text-muted">Verified: {{ format_detail_value(field_run.verified_value) }}</div>
                  <div v-if="field_run.reviewer_comment" class="small text-muted">Comment: {{ field_run.reviewer_comment }}</div>
                </div>
              </div>
            </div>

            </div>
          </article>
        </div>
        </template>
          </div>
        </div>
      </div>
    </div>
      </section>
    </template>
  </template>

  <div
    v-if="manual_review_modal.visible"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @click.self="close_manual_review_modal"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow verification-manual-review-modal">
        <div class="modal-header verification-manual-review-modal__header">
          <div>
            <div class="text-uppercase text-muted fw-semibold small mb-1">Manual verification</div>
            <h2 class="h5 fw-bold text-black mb-0">{{ manual_review_modal.item?.check || 'Review decision' }}</h2>
          </div>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            :disabled="manual_review_modal.is_submitting"
            @click="close_manual_review_modal"
          ></button>
        </div>
        <div class="modal-body verification-manual-review-modal__body">
          <section class="verification-manual-review-summary mb-4">
            <div class="verification-manual-review-summary__hero">
              <div>
                <div class="text-uppercase fw-semibold small verification-manual-review-summary__eyebrow">Reviewer workspace</div>
                <h3 class="verification-manual-review-summary__title">Review the submitted data against evidence and record one final outcome.</h3>
                <p class="verification-manual-review-summary__copy mb-0">
                  Work through the field cards below, save each field result, then capture the decision once the case is fully reviewed.
                </p>
              </div>
              <div class="verification-manual-review-summary__progress">
                <div class="verification-manual-review-summary__progress-value">{{ manual_review_progress.completion_percentage }}%</div>
                <div class="verification-manual-review-summary__progress-label">Review completion</div>
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-4">
                <div class="verification-manual-review-meta-card">
                  <div class="text-uppercase text-muted fw-semibold small mb-1">Run ID</div>
                  <div class="fw-semibold text-black">{{ manual_review_modal.run?.id || '-' }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="verification-manual-review-meta-card">
                  <div class="text-uppercase text-muted fw-semibold small mb-1">Status</div>
                  <div class="fw-semibold text-black">{{ manual_review_modal.run?.status || manual_review_modal.item?.status || '-' }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="verification-manual-review-meta-card">
                  <div class="text-uppercase text-muted fw-semibold small mb-1">Started</div>
                  <div class="fw-semibold text-black">{{ format_detail_value(manual_review_modal.run?.started_at || manual_review_modal.item?.last_run) }}</div>
                </div>
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div
                v-for="card in manual_review_summary_cards()"
                :key="card.label"
                class="col-md-3 col-sm-6"
              >
                <div :class="['verification-manual-review-stat-card', `is-${card.tone}`]">
                  <div class="verification-manual-review-stat-card__label">{{ card.label }}</div>
                  <div class="verification-manual-review-stat-card__value">{{ card.value }}</div>
                  <div class="verification-manual-review-stat-card__detail">{{ card.detail }}</div>
                </div>
              </div>
            </div>
          </section>

          <div v-if="get_manual_review_form_sections().length" class="mb-4">
            <div class="text-uppercase text-muted fw-semibold small mb-2">Assigned manual verification questions</div>
            <div class="accordion" id="manual-review-forms-accordion">
              <div
                v-for="form_section in get_manual_review_form_sections()"
                :key="form_section.id"
                class="accordion-item border rounded-3 overflow-hidden mb-2"
              >
                <h2 class="accordion-header" :id="`manual-review-form-heading-${form_section.id}`">
                  <button
                    class="accordion-button collapsed fw-semibold"
                    type="button"
                    data-bs-toggle="collapse"
                    :data-bs-target="`#manual-review-form-body-${form_section.id}`"
                    aria-expanded="false"
                    :aria-controls="`manual-review-form-body-${form_section.id}`"
                  >
                    <span>{{ form_section.form_name }}</span>
                    <span class="ms-2 text-muted small">{{ form_section.answered_count }} answered</span>
                  </button>
                </h2>
                <div
                  :id="`manual-review-form-body-${form_section.id}`"
                  class="accordion-collapse collapse"
                  :aria-labelledby="`manual-review-form-heading-${form_section.id}`"
                  data-bs-parent="#manual-review-forms-accordion"
                >
                  <div class="accordion-body bg-white">
                    <div
                      v-for="section in form_section.sections"
                      :key="section.id"
                      class="mb-3"
                    >
                      <div class="fw-semibold text-black mb-2">{{ section.label }}</div>

                      <div
                        v-for="sub_section in section.sub_sections"
                        :key="sub_section.id"
                        class="mb-3"
                      >
                        <div class="text-muted small text-uppercase fw-semibold mb-2">{{ sub_section.label }}</div>
                        <div class="d-grid gap-2">
                          <div
                            v-for="question in sub_section.questions"
                            :key="question.id"
                            class="verification-manual-review-question-card"
                          >
                            <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                              <div class="min-w-0">
                                <div class="fw-semibold text-black">{{ question.label }}</div>
                                <div class="small text-muted mt-2">Submitted answer</div>
                                <div class="verification-manual-review-question-card__answer">
                                  {{ format_detail_value(question.answer) }}
                                </div>
                              </div>
                              <div class="d-flex flex-column align-items-end gap-2 flex-shrink-0 verification-manual-review-question-card__actions">
                                <span :class="['badge rounded-pill fw-normal', status_badge_class(manual_field_run_badge_tone(question.id))]">
                                  {{ manual_field_run_badge_label(question.id) }}
                                </span>
                                <button
                                  v-if="!is_entity_verification_read_only"
                                  type="button"
                                  class="onboarding-queue-view-button verification-manual-review-question-card__button"
                                  @click="open_manual_review_field_form(question)"
                                >
                                  {{ has_manual_field_run(question.id) ? 'Edit review' : 'Review field' }}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="alert alert-light border mb-4">
            No assigned manual verification questions were returned for this run.
          </div>

          <div v-if="manual_review_modal.field_form && !is_entity_verification_read_only" class="verification-manual-review-field-editor mb-4">
            <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
              <div>
                <div class="text-uppercase text-muted fw-semibold small mb-1">Field review</div>
                <div class="fw-semibold text-black">{{ manual_review_modal.field_form.field_label }}</div>
              </div>
              <button
                type="button"
                class="btn-close"
                aria-label="Close"
                :disabled="manual_review_modal.is_saving_field"
                @click="close_manual_review_field_form"
              ></button>
            </div>

            <div class="row g-3">
              <div class="col-12">
                <label class="form-label fw-semibold">Submitted answer</label>
                <input
                  :value="manual_review_modal.field_form.user_answer"
                  type="text"
                  class="form-control"
                  readonly
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Verified value</label>
                <input
                  v-model="manual_review_modal.field_form.verified_value"
                  type="text"
                  class="form-control"
                  placeholder="Enter the value confirmed from evidence"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Match status</label>
                <select v-model="manual_review_modal.field_form.match_status" class="form-select">
                  <option value="match">Match</option>
                  <option value="mismatch">Mismatch</option>
                  <option value="partial">Partial</option>
                  <option value="unable_to_verify">Unable to verify</option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label fw-semibold">Evidence reference</label>
                <input
                  v-model="manual_review_modal.field_form.source_evidence"
                  type="text"
                  class="form-control"
                  placeholder="e.g. CIPC report, bank letter, site visit note"
                />
              </div>
              <div class="col-12">
                <label class="form-label fw-semibold">Reviewer comment</label>
                <textarea
                  v-model="manual_review_modal.field_form.reviewer_comment"
                  class="form-control"
                  rows="3"
                  placeholder="Explain what was checked and any discrepancy found."
                ></textarea>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                class="btn btn-light"
                :disabled="manual_review_modal.is_saving_field"
                @click="close_manual_review_field_form"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="manual_review_modal.is_saving_field"
                @click="submit_manual_field_result"
              >
                {{ manual_review_modal.is_saving_field ? 'Saving...' : 'Save field review' }}
              </button>
            </div>
          </div>

          <div v-if="get_manual_field_runs(manual_review_modal.item).length" class="mb-4">
            <div class="text-uppercase text-muted fw-semibold small mb-2">Captured field results</div>
            <div class="d-grid gap-2">
              <div
                v-for="field_run in get_manual_field_runs(manual_review_modal.item)"
                :key="field_run.id"
                class="verification-manual-review-result-card"
              >
                <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                  <div>
                    <div class="fw-semibold text-black">{{ field_run.field_label_snapshot || 'Reviewed field' }}</div>
                    <div class="small text-muted mt-2">Submitted: {{ format_detail_value(field_run.user_answer) }}</div>
                    <div class="small text-muted">Verified: {{ format_detail_value(field_run.verified_value) }}</div>
                    <div class="small text-muted">Outcome: {{ format_match_status(field_run.match_status) }}</div>
                  </div>
                  <span :class="['badge rounded-pill fw-normal', status_badge_class(normalize_match_status(field_run.match_status) === 'match' ? 'success' : normalize_match_status(field_run.match_status) === 'mismatch' ? 'danger' : 'warning')]">
                    {{ format_match_status(field_run.match_status) }}
                  </span>
                </div>
                <div v-if="field_run.reviewer_comment" class="small text-muted">Comment: {{ field_run.reviewer_comment }}</div>
              </div>
            </div>
          </div>

          <section v-if="!is_entity_verification_read_only" class="verification-manual-review-decision">
            <div class="text-uppercase text-muted fw-semibold small mb-2">Final decision</div>
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label fw-semibold">Decision</label>
                <select v-model="manual_review_modal.decision_status" class="form-select">
                  <option value="pass">Pass</option>
                  <option value="fail">Fail</option>
                  <option value="partial">Partial</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div class="col-md-8">
                <label class="form-label fw-semibold">Reviewer notes</label>
                <textarea
                  v-model="manual_review_modal.decision_reason"
                  class="form-control"
                  rows="4"
                  placeholder="Add the verification outcome, evidence reviewed, or remediation needed."
                ></textarea>
              </div>
            </div>
          </section>
        </div>
        <div v-if="!is_entity_verification_read_only" class="modal-footer verification-manual-review-modal__footer">
          <button
            type="button"
            class="btn btn-light"
            :disabled="manual_review_modal.is_submitting"
            @click="close_manual_review_modal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-success"
            :disabled="manual_review_modal.is_submitting"
            @click="submit_manual_review_decision"
          >
            {{ manual_review_modal.is_submitting ? 'Saving...' : 'Save decision' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="manual_review_modal.visible" class="modal-backdrop fade show"></div>

  <AgreementSignModal
    :visible="sign_modal.visible"
    :agreement-title="sign_modal.agreement_title"
    :signatory-name="sign_modal.signatory_name"
    :document-id="sign_modal.document_id"
    @close="close_sign_modal"
    @signed="complete_signature"
  />

  <div
    v-if="document_preview_modal.visible"
    class="onboarding-case-detail-backdrop"
    @click="close_document_preview"
  ></div>

  <section
    v-if="document_preview_modal.visible"
    class="queue-form-preview-modal"
    role="dialog"
    aria-modal="true"
    :aria-label="document_preview_modal.title || 'Document preview'"
  >
    <header class="queue-form-preview-modal__header">
      <div>
        <div class="queue-form-preview-modal__eyebrow">Document preview</div>
        <h2 class="queue-form-preview-modal__title">{{ document_preview_modal.title }}</h2>
      </div>
      <div class="d-flex align-items-center gap-3">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="download_document(document_preview_modal.detail || {})">Download</button>
        <button
          type="button"
          class="queue-form-preview-modal__close"
          aria-label="Close document preview"
          @click="close_document_preview"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </header>

    <div class="queue-form-preview-modal__body">
      <img
        v-if="document_preview_modal.type === 'image'"
        :src="preview_modal_src()"
        alt="Selected document preview"
        class="queue-form-preview-modal__image"
      >
      <iframe
        v-else-if="document_preview_modal.type === 'pdf'"
        :src="preview_modal_src()"
        title="Document preview"
        class="queue-form-preview-modal__frame"
      ></iframe>
      <div v-else class="queue-form-preview-modal__fallback">
        <i class="bi bi-file-earmark-text"></i>
        <p class="mb-0">Preview is not available for this file type.</p>
      </div>
    </div>
  </section>
</main>
